import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, X, TrashSimple, Receipt, GasPump, PencilLine, DotsSix,
  MagnifyingGlass, Copy, FunnelSimple, FilePdf, DownloadSimple, PencilSimple, Printer,
} from "@phosphor-icons/react";
import { MultiFilePicker } from "@/components/MultiFilePicker";
import { extractFromFile } from "@/lib/invoiceExtractor";
import { InvSuggestion, AmountSuggestion } from "./TCAndTCSR";
import { printFilterSummary } from "@/lib/printSummary";

const BUILTIN_ICONS = { petrol: GasPump, stationary: PencilLine, others: DotsSix };
const catIcon = (v) => BUILTIN_ICONS[v] || DotsSix;
function fmtDate(s) {
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Expenses() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]); // [{value,label,is_builtin}]
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [sortKey, setSortKey] = useState(params.get("sort") || "date");
  const [sortDir, setSortDir] = useState(params.get("dir") === "asc" ? "asc" : "desc");

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (category) next.category = category;
    if (from) next.from = from;
    if (to) next.to = to;
    if (sortKey) { next.sort = sortKey; next.dir = sortDir; }
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, from, to, sortKey, sortDir]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/expense-categories");
      setCategories(data);
    } catch (e) { /* silent */ }
  };
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: x }, { data: c }] = await Promise.all([
        api.get("/expenses"),
        api.get("/expense-categories"),
      ]);
      setItems(x);
      setCategories(c);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catLabel = (v) => (categories.find((c) => c.value === v)?.label) || v;

  const del = async (x) => {
    if (!window.confirm(`Delete expense (${catLabel(x.category)} · RM ${Number(x.amount).toFixed(2)})?`)) return;
    try {
      await api.delete(`/expenses/${x.id}`);
      setItems((xs) => xs.filter((v) => v.id !== x.id));
      toast.success("Expense deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openReceipt = (id) => {
    const t = localStorage.getItem("access_token") || "";
    window.open(`${API}/expenses/${id}/receipt/download?auth=${encodeURIComponent(t)}`, "_blank");
  };
  const shareLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Filtered link copied — share it with your team");
      return;
    } catch {}
    try {
      const ta = document.createElement("textarea");
      ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Filtered link copied — share it with your team");
    } catch { toast.error("Could not copy — copy the URL manually"); }
  };
  const clearFilters = () => { setQ(""); setCategory(""); setFrom(""); setTo(""); setSortKey("date"); setSortDir("desc"); };

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((x) => {
      if (needle) {
        const label = catLabel(x.category).toLowerCase();
        const hay = `${x.description || ""} ${x.category || ""} ${label} ${x.receipt_filename || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (category && x.category !== category) return false;
      if (from && (x.date || "") && x.date < from) return false;
      if (to   && (x.date || "") && x.date > to)   return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, q, category, from, to, categories]);

  const displayed = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const getV = (x) => {
      switch (sortKey) {
        case "date":        return x.date || "";
        case "category":    return (catLabel(x.category) || "").toLowerCase();
        case "description": return (x.description || "").toLowerCase();
        case "amount":      return Number(x.amount) || 0;
        default: return "";
      }
    };
    return [...filtered].sort((a, b) => {
      const va = getV(a), vb = getV(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, sortDir, categories]);

  const totalAll = displayed.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  // Show up to 3 category tiles (in insertion order), rest folded into "Others cats"
  const catTiles = categories.slice(0, 3).map((c) => ({
    ...c,
    total: displayed.filter((x) => x.category === c.value).reduce((s, x) => s + (Number(x.amount) || 0), 0),
    count: displayed.filter((x) => x.category === c.value).length,
  }));
  const hasFilters = q || category || from || to;

  const onPrint = () => {
    printFilterSummary({
      title: "Expenses — Filter Summary",
      filters: [
        q && `Search: "${q}"`,
        category && `Category: ${catLabel(category)}`,
        from && `From: ${from}`,
        to && `To: ${to}`,
      ].filter(Boolean),
      columns: [
        { key: "date",        label: "Date",        format: fmtDate },
        { key: "category",    label: "Category",    format: (v) => catLabel(v) },
        { key: "inv_no",      label: "Inv / Receipt No." },
        { key: "description", label: "Description" },
        { key: "receipt_filename", label: "Receipt" },
        { key: "amount",      label: "Amount (RM)", align: "right", format: (v) => Number(v || 0).toFixed(2) },
      ],
      rows: displayed,
      total: totalAll,
      totalLabel: "Total Amount",
    });
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Cashbook</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">Expenses</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Log daily operating expenses with a receipt PDF. Categories are extensible — add your own when needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} data-testid="expense-print-btn"
            className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150">
            <Printer size={14} /> Print filter summary
          </button>
          <button onClick={shareLink} data-testid="expense-share-btn"
            className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150">
            <Copy size={14} /> Copy share link
          </button>
          <button onClick={() => setShowNew(true)} data-testid="new-expense-btn"
            className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150">
            <Plus size={16} /> New Expense
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="expense-filters">
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search description, category, file…"
            data-testid="expense-search"
            className="w-full text-sm border border-zinc-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#002FA7]" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="expense-category-filter"
          className="text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          data-testid="expense-from"
          className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          data-testid="expense-to"
          className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
      </div>
      {hasFilters && (
        <div className="text-xs text-zinc-500 font-mono mb-3 flex items-center gap-3" data-testid="expense-filter-status">
          <FunnelSimple size={12} /> Showing {displayed.length} of {items.length}
          <button onClick={clearFilters} data-testid="expense-clear-filters" className="text-[#002FA7] hover:underline">Clear filters</button>
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="expense-summary">
        <div className="bg-white border border-zinc-200 px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">Filtered Total</div>
          <div className="font-display font-black text-2xl text-zinc-950" data-testid="expense-filtered-total">RM {totalAll.toFixed(2)}</div>
          <div className="text-[10px] font-mono text-zinc-400">{displayed.length} entr{displayed.length === 1 ? "y" : "ies"}</div>
        </div>
        {catTiles.map((c) => {
          const Icon = catIcon(c.value);
          return (
            <div key={c.value} className="bg-white border border-zinc-200 px-4 py-3" data-testid={`expense-cat-${c.value}`}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">
                <Icon size={12} weight="duotone" /> {c.label}
              </div>
              <div className="font-display font-black text-2xl text-zinc-950">RM {c.total.toFixed(2)}</div>
              <div className="text-[10px] font-mono text-zinc-400">{c.count} entr{c.count === 1 ? "y" : "ies"}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <SortTh label="Date"        k="date"        sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("date")} />
              <SortTh label="Category"    k="category"    sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("category")} />
              <SortTh label="Description" k="description" sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("description")} />
              <th className="text-left px-4 py-3">Receipt</th>
              <SortThRight label="Amount (RM)" k="amount" sortKey={sortKey} dir={sortDir} onClick={() => toggleSort("amount")} />
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            ) : displayed.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-16 text-zinc-500">
                <Receipt size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
                <div className="font-mono text-sm">{items.length === 0 ? "No expenses yet — add the first one." : "No expenses match these filters."}</div>
              </td></tr>
            ) : displayed.map((x) => {
              const Icon = catIcon(x.category);
              return (
                <tr key={x.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`expense-row-${x.id}`}>
                  <td className="px-4 py-3 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(x.date)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-indigo-200 bg-indigo-50 text-indigo-800 px-2 py-0.5 uppercase">
                      <Icon size={12} weight="duotone" /> {catLabel(x.category)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-800 truncate max-w-[380px]" title={x.description}>{x.description || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3">
                    {x.receipt_filename ? (
                      <button onClick={() => openReceipt(x.id)} data-testid={`expense-receipt-${x.id}`}
                        className="inline-flex items-center gap-1 text-[#002FA7] text-xs hover:underline">
                        <FilePdf size={14} weight="duotone" /> <span className="truncate max-w-[180px]">{x.receipt_filename}</span>
                      </button>
                    ) : <span className="text-zinc-400 font-mono text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-900 font-medium text-right whitespace-nowrap">{Number(x.amount).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {x.receipt_filename && (
                      <button onClick={() => openReceipt(x.id)} data-testid={`expense-download-${x.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150">
                        <DownloadSimple size={14} /> PDF
                      </button>
                    )}
                    <button onClick={() => setEditing(x)} data-testid={`expense-edit-${x.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150 ml-1">
                      <PencilSimple size={14} /> Edit
                    </button>
                    {(user?.role === "admin" || x.created_by_id === user?.id) && (
                      <button onClick={() => del(x)} data-testid={`expense-delete-${x.id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 transition-colors duration-150 ml-1">
                        <TrashSimple size={14} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {displayed.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-300 bg-zinc-50">
                <td colSpan={4} className="px-4 py-3 text-right text-[10px] uppercase font-mono text-zinc-500">Total</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-zinc-950" data-testid="expense-total">RM {totalAll.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showNew && (
        <NewExpenseModal
          categories={categories}
          canAddCategory={user?.role === "admin"}
          onCategoryAdded={loadCategories}
          onClose={() => setShowNew(false)}
          onCreated={(xs2) => { setItems((xs) => [...xs2, ...xs]); setShowNew(false); }}
        />
      )}
      {editing && (
        <EditExpenseModal
          expense={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={(u) => { setItems((xs) => xs.map(x => x.id === u.id ? { ...x, ...u } : x)); setEditing(null); }}
        />
      )}
    </Layout>
  );
}

function SortTh({ label, k, sortKey, dir, onClick }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return (
    <th className="text-left px-4 py-3 select-none">
      <button type="button" onClick={onClick} data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active ? "text-zinc-900" : "text-zinc-500"}`}>
        {label}<span className={`font-mono text-[11px] ${active ? "text-[#002FA7]" : "text-zinc-400"}`}>{arrow}</span>
      </button>
    </th>
  );
}
function SortThRight({ label, k, sortKey, dir, onClick }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return (
    <th className="text-right px-4 py-3 select-none">
      <button type="button" onClick={onClick} data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active ? "text-zinc-900" : "text-zinc-500"}`}>
        {label}<span className={`font-mono text-[11px] ${active ? "text-[#002FA7]" : "text-zinc-400"}`}>{arrow}</span>
      </button>
    </th>
  );
}

function NewExpenseModal({ categories, canAddCategory, onCategoryAdded, onClose, onCreated }) {
  const [category, setCategory] = useState(categories[0]?.value || "petrol");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [invNo, setInvNo] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [invSuggestion, setInvSuggestion] = useState("");
  const [amtSuggestion, setAmtSuggestion] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannedNames = useRef(new Set());

  useEffect(() => {
    const f = files[files.length - 1];
    if (!f || scannedNames.current.has(f.name)) return;
    scannedNames.current.add(f.name);
    (async () => {
      setScanning(true);
      const { invoiceNumber, amount: amt } = await extractFromFile(f);
      setScanning(false);
      if (invoiceNumber) {
        setInvSuggestion(invoiceNumber);
        setInvNo(cur => cur.trim() ? cur : invoiceNumber);
      }
      if (amt != null) {
        setAmtSuggestion(amt);
        setAmount(cur => (cur && Number(cur) > 0) ? cur : String(amt));
      }
    })();
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  const createCategory = async () => {
    const label = newCatLabel.trim();
    if (!label) { toast.error("Category name required"); return; }
    setCreatingCat(true);
    try {
      const { data } = await api.post("/expense-categories", { label });
      await onCategoryAdded();
      setCategory(data.value);
      setNewCatLabel("");
      setAddingCat(false);
      toast.success(`Category "${data.label}" added`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreatingCat(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    if (!date) { toast.error("Date is required"); return; }
    setSaving(true);
    try {
      const created = [];
      const rounds = files.length || 1;
      for (let i = 0; i < rounds; i++) {
        const fd = new FormData();
        fd.append("category", category);
        fd.append("date", date);
        fd.append("amount", String(amt));
        fd.append("description", description.trim());
        fd.append("inv_no", invNo.trim());
        if (files[i]) fd.append("file", files[i]);
        const { data } = await api.post("/expenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
        created.push(data);
      }
      toast.success(created.length === 1 ? "Expense added" : `${created.length} expenses added`);
      onCreated(created);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-expense-modal">
      <form onSubmit={submit} className="bg-white border border-zinc-200 shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Receipt size={18} className="text-[#002FA7]" />
            <div className="font-display font-black text-lg tracking-tight text-zinc-950">New Expense</div>
          </div>
          <button type="button" onClick={onClose} data-testid="expense-close-btn" className="text-zinc-500 hover:text-zinc-900 p-1">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Fld label="Category">
            <div className="flex items-center gap-2">
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="expense-category-select"
                className="flex-1 text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
                {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {canAddCategory && (
                <button type="button" onClick={() => setAddingCat((v) => !v)} data-testid="expense-add-category-btn"
                  className="bg-white border border-zinc-200 text-zinc-700 px-2.5 py-2 hover:bg-zinc-50 transition-colors duration-150"
                  title="Add new category">
                  <Plus size={14} />
                </button>
              )}
            </div>
            {addingCat && (
              <div className="mt-2 flex items-center gap-2" data-testid="expense-new-category-row">
                <input value={newCatLabel} onChange={(e) => setNewCatLabel(e.target.value)}
                  placeholder="e.g. Toll, Meals, Repair…" data-testid="expense-new-category-input"
                  className="flex-1 text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
                <button type="button" onClick={createCategory} disabled={creatingCat} data-testid="expense-new-category-save"
                  className="text-sm bg-[#002FA7] text-white px-3 py-2 hover:bg-[#00227A] disabled:opacity-50">
                  {creatingCat ? "…" : "Add"}
                </button>
              </div>
            )}
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Date" required>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="expense-date-input"
                className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
            </Fld>
            <Fld label="Amount (RM)" required>
              <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="expense-amount-input"
                placeholder="0.00"
                className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
              <AmountSuggestion scanning={scanning} suggestion={amtSuggestion} current={amount} onUse={()=>{setAmount(String(amtSuggestion)); setAmtSuggestion(null);}} onDismiss={()=>setAmtSuggestion(null)}/>
            </Fld>
          </div>
          <Fld label="Description">
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="expense-desc-input"
              className="w-full text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
          </Fld>
          <Fld label="Invoice / Receipt No.">
            <input value={invNo} onChange={(e) => setInvNo(e.target.value)} data-testid="expense-inv-input" placeholder="Auto-filled from file when possible"
              className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
            <InvSuggestion scanning={scanning} suggestion={invSuggestion} invNo={invNo} onUse={()=>{setInvNo(invSuggestion); setInvSuggestion("");}} onDismiss={()=>setInvSuggestion("")}/>
          </Fld>
          <Fld label="Receipts (optional — any file type, creates one expense per file)">
            <MultiFilePicker files={files} onChange={setFiles} testid="expense-file" hint="PDF, image, Excel, ZIP, Word…"/>
          </Fld>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3 bg-zinc-50">
          <button type="button" onClick={onClose} data-testid="expense-cancel-btn"
            className="text-sm border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-100 transition-colors duration-150">Cancel</button>
          <button type="submit" disabled={saving} data-testid="expense-submit-btn"
            className="text-sm bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] disabled:opacity-50 transition-colors duration-150">
            {saving ? "Saving…" : "Save expense"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Fld({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">
        {label}{required && <span className="text-[#FF4F00]"> *</span>}
      </div>
      {children}
    </label>
  );
}

function EditExpenseModal({ expense, categories, onClose, onSaved }) {
  const [category, setCategory] = useState(expense.category || categories[0]?.value || "petrol");
  const [date, setDate] = useState(expense.date || "");
  const [amount, setAmount] = useState(expense.amount != null ? String(expense.amount) : "");
  const [invNo, setInvNo] = useState(expense.inv_no || "");
  const [description, setDescription] = useState(expense.description || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    if (!date) { toast.error("Date is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/expenses/${expense.id}`, {
        category, date, amount: amt, inv_no: invNo.trim(), description: description.trim(),
      });
      toast.success("Expense updated"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-expense-modal">
      <form onSubmit={submit} className="bg-white border border-zinc-200 shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <div className="font-display font-black text-lg tracking-tight text-zinc-950">Edit Expense</div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 p-1"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <Fld label="Category">
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="expense-edit-category" className="w-full text-sm border border-zinc-200 px-3 py-2 bg-white">
              {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Date" required><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="expense-edit-date" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
            <Fld label="Amount (RM)" required><input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="expense-edit-amount" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
          </div>
          <Fld label="Invoice / Receipt No."><input value={invNo} onChange={(e) => setInvNo(e.target.value)} data-testid="expense-edit-inv" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
          <Fld label="Description"><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="expense-edit-desc" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3 bg-zinc-50">
          <button type="button" onClick={onClose} className="text-sm border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-100">Cancel</button>
          <button type="submit" disabled={saving} data-testid="expense-edit-save" className="text-sm bg-[#002FA7] text-white px-4 py-2 hover:bg-[#00227A] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
